import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import type { SurveyQuestionDto } from '@/shared/api/types'
import {
  completeness, getCaptureSnapshot, isSubQuestion, openCapture, orderedOptions, subQuestions,
  subscribeCapture, walk,
  type CaptureSnapshot, type Completeness, type Walk,
} from './captureSession'
import { getQueueSnapshot, subscribeQueue, type QueueSnapshot } from './captureQueue'

/**
 * React's window onto the interview and the queue.
 *
 * There is no state in this file and that is the design. Both stores outlive
 * every component that shows them — an interview survives navigation, a queued
 * submission survives a reload — so `useSyncExternalStore` is the right
 * primitive: a tearing-free read of an external store, with no effect to
 * subscribe and therefore no cleanup that can accidentally end a recording.
 *
 * ## Why the derived values are `useMemo` and not part of the snapshot
 *
 * `walk()` and `completeness()` build fresh objects on every call. Returning one
 * from a `getSnapshot` would fail React's identity check on every render and spin
 * forever — the classic `useSyncExternalStore` infinite loop. So the STORE holds
 * only the primitive facts, and everything derived from them is memoised here
 * against the snapshot that produced it.
 */

export function useCaptureSession(): CaptureSnapshot {
  return useSyncExternalStore(subscribeCapture, getCaptureSnapshot, getCaptureSnapshot)
}

export function useCaptureQueue(): QueueSnapshot {
  return useSyncExternalStore(subscribeQueue, getQueueSnapshot, getQueueSnapshot)
}

/**
 * Open the poll named in the query string.
 *
 * `openCapture` is idempotent for the same pair, so a remount does not restart an
 * interview — which is exactly what an unmount-and-remount from navigation would
 * otherwise do, discarding the answers and minting a new idempotency key.
 */
export function useOpenCapture(surveyId: string | null, assignmentId: string | null): void {
  useEffect(() => {
    if (!surveyId) return
    void openCapture(surveyId, assignmentId)
  }, [surveyId, assignmentId])
}

export function useWalk(session: CaptureSnapshot): Walk {
  // Keyed on the two things that can change which questions are visible: the
  // instrument itself, and the answers the branches read.
  return useMemo(() => walk(), [session.bundle, session.answers])
}

export function useCompleteness(session: CaptureSnapshot, clipCount: number): Completeness {
  return useMemo(
    () => completeness(),
    [session.bundle, session.answers, clipCount],
  )
}

/** The options for one question, in the order this respondent will see them. */
export function useOrderedOptions(
  question: SurveyQuestionDto | null,
  seed: number,
  randomiseGlobally: boolean,
) {
  return useMemo(
    () => (question ? orderedOptions(question, seed, randomiseGlobally) : []),
    [question, seed, randomiseGlobally],
  )
}

/** A MATRIX's rows, in position order. Empty for every other type. */
export function useSubQuestions(
  all: SurveyQuestionDto[], parent: SurveyQuestionDto | null,
): SurveyQuestionDto[] {
  return useMemo(
    () => (parent && parent.typeCode === 'MATRIX' ? subQuestions(all, parent) : []),
    [all, parent],
  )
}

/**
 * Seconds since an ISO instant, ticking.
 *
 * Derived from the timestamp rather than counted up from a mount, so navigating
 * away and back shows the real elapsed time instead of restarting at zero — the
 * interview clock has to agree with `clientDurationSeconds`.
 */
export function useElapsedSince(startedAt: string | null): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!startedAt) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [startedAt])
  if (!startedAt) return 0
  const started = new Date(startedAt).getTime()
  return Number.isNaN(started) ? 0 : Math.max(0, Math.floor((now - started) / 1000))
}

export { isSubQuestion }
export { useRecorder } from './AudioRecorder'
